import { useState } from "react";

/**
 * `FieldsScreen`/`FrScreen`/`RenameScreen` are mounted on real routes (`AppRouter.tsx`), so
 * switching between header tabs unmounts whichever screen you're leaving — a plain `useState`
 * there loses any unsaved edits. This mirrors the value into
 * a module-level store keyed by `key`, so the NEXT mount (switching back) picks up where the
 * previous one left off. Scoped to this SPA session only — a full page reload clears it, same as
 * any other in-memory unsaved-changes UX.
 */
const store = new Map<string, unknown>();

export const usePersistedState = <T>(
  key: string,
  initial: T
): [T, (next: T | ((prev: T) => T)) => void] => {
  const [value, setValue] = useState<T>(() =>
    store.has(key) ? (store.get(key) as T) : initial
  );

  const setPersisted = (next: T | ((prev: T) => T)): void => {
    setValue((prev) => {
      const resolved =
        typeof next === "function" ? (next as (prev: T) => T)(prev) : next;
      store.set(key, resolved);
      return resolved;
    });
  };

  return [value, setPersisted];
};
